import React, { useCallback, useEffect, useMemo, useState } from "react";

import { Alert } from "@mui/lab";
import {
  Button,
  Checkbox,
  FormControlLabel,
  Grid,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import { useParams } from "react-router-dom";
import mainStyles from "styles";

import GenericDialog from "../../../shared/generic-dialog";
import { restoreSites } from "api/endpoints/data-governance";
import { callOptionsHeaders } from "api/helpers";
import { toUUID, validateUUID } from "helpers/s12-helpers";
import { UrlParamTypes } from "types/org-display";

const RestoreSites = () => {
  const { regionCode, roleId } = useParams<UrlParamTypes>();

  const classes = mainStyles();
  const theme = useTheme();

  const [modalOpen, setModalOpen] = useState(false);
  const [siteIds, setSiteIds] = useState("");
  const [withMembers, setWithMembers] = useState(true);
  const [withDescendants, setWithDescendants] = useState(true);
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [success, setSuccess] = useState("");

  useEffect(() => {
    setError("");
    setSuccess("");
    setSiteIds("");
    setWithMembers(true);
  }, [modalOpen]);

  const validIDs = useMemo(() => {
    if (!!siteIds.trim()) {
      const ids = siteIds.split(",");
      for (let i = 0; i < siteIds.length; i++) {
        if (ids[i]?.trim() && !validateUUID(toUUID(ids[i].trim()))) {
          return false;
        }
      }
    }
    return true;
  }, [siteIds]);

  const toUUIDList = useCallback((scIds: string): string[] => {
    const ids = scIds.split(",") || [];
    return ids.map((id) => toUUID(id.trim()));
  }, []);

  const handleRestoreSites = async (
    regionCode: string,
    roleId: string,
    siteIds: string,
    withMembers: boolean,
    withDescendants: boolean,
  ) => {
    setIsLoading(true);
    try {
      const resp = await restoreSites(
        {
          orgId: toUUID(roleId),
          siteIds: toUUIDList(siteIds),
          withMembers,
          withDescendants,
        },
        callOptionsHeaders(regionCode, roleId),
      );
      setError("");
      setSiteIds("");
      setSuccess(`Successfully restored ${resp.folderId.length} sites`);
    } catch (e) {
      // eslint-disable-next-line
      setError((e as any).message);
    }
    setIsLoading(false);
  };

  const alert = useMemo(() => {
    const message =
      "Enter the site IDs you want to restore. You can enter multiple IDs separated by commas";
    return (
      <Alert
        severity={error ? "error" : success ? "success" : "info"}
        variant="filled"
        data-testid="alert-message"
        sx={{ ...classes.spaceTopSmall, ...classes.spaceBottomSmall }}
      >
        <Typography variant="subtitle2">
          {error ? error : success ? success : message}
        </Typography>
      </Alert>
    );
  }, [error, success, classes]);

  return (
    <>
      <Button
        sx={{ ...classes.spaceTopSmall, ...classes.spaceRightMedium }}
        variant="contained"
        color="primary"
        fullWidth
        onClick={() => setModalOpen(true)}
        data-testid="restore-sites-btn"
      >
        Restore Deleted Sites
      </Button>
      <GenericDialog
        title="Restore deleted sites"
        open={modalOpen}
        hasAckBtn
        disabled={!siteIds || !validIDs}
        handleAckBtnClick={() => {
          handleRestoreSites(
            regionCode,
            roleId,
            siteIds,
            withMembers,
            withDescendants,
          );
        }}
        hasXCloseBtn
        acknowledgeBtnLabel={"Restore sites"}
        handleDialogClose={() => setModalOpen(false)}
        isLoading={isLoading}
      >
        <Grid
          container
          display="flex"
          flexDirection="column"
          data-testid="restore-modal-container"
        >
          <Grid item>
            {alert}
            <TextField
              sx={classes.spaceTop}
              id="site-id"
              data-testid="site-id"
              label="Site IDs"
              placeholder="location_1234, location_5678 ..."
              value={siteIds}
              onChange={(event) => setSiteIds(event.target.value)}
              variant="outlined"
              color={!validIDs ? "error" : "primary"}
              multiline
              size="small"
              fullWidth
              inputProps={{ "data-testid": "site-id-input" }}
              onKeyUp={(event) => {
                if (event.key === "Enter" && !!siteIds && validIDs) {
                  handleRestoreSites(
                    regionCode,
                    roleId,
                    siteIds,
                    withMembers,
                    withDescendants,
                  );
                }
              }}
            />
            {!validIDs && (
              <Typography
                align={"left"}
                variant="subtitle2"
                color={theme.palette.error.main}
                data-testid="invalid-id"
              >
                Site IDs are not in a valid format
              </Typography>
            )}
          </Grid>
          <Grid item>
            <FormControlLabel
              checked={withMembers}
              sx={classes.spaceTop}
              control={<Checkbox />}
              label="Restore user memberships"
              onClick={(e) => {
                e.preventDefault();
                setWithMembers(!withMembers);
              }}
            />
          </Grid>
          <Grid item>
            <FormControlLabel
              checked={withDescendants}
              sx={classes.spaceTop}
              control={<Checkbox />}
              label="Restore descendants"
              onClick={(e) => {
                e.preventDefault();
                setWithDescendants(!withDescendants);
              }}
            />
          </Grid>
        </Grid>
      </GenericDialog>
    </>
  );
};

export default RestoreSites;
